import * as v from 'valibot'

/**
 * Clients — the other side of every job.
 *
 * A client is a first-class record: who they are, where they live on Discord,
 * who to reach, and how the work relationship stands. Everything on a client
 * is editable in place for as long as the relationship exists; archiving
 * withdraws the client from the working set without deleting anything, the
 * same rule accounts follow.
 *
 * The status vocabulary, and what each word means:
 *
 *   active     the relationship is live; jobs can be opened for them
 *   inactive   real, but nothing happening right now
 *   archived   withdrawn from the working set; restorable, never deleted
 */

export const CLIENT_STATUSES = ['active', 'inactive', 'archived'] as const
export type ClientStatus = (typeof CLIENT_STATUSES)[number]

/** A client, as the console sees it. */
export interface Client {
  id: string
  /** The name the studio uses for them, day to day. */
  name: string
  companyName: string | null
  /** The Discord server: the snowflake is the stable key, the name the label. */
  discordServerId: string | null
  discordServerName: string | null
  contactPerson: string | null
  contactEmail: string | null
  contactPhone: string | null
  websiteUrl: string | null
  countryCode: string | null
  timezone: string | null
  notes: string | null
  /** The studio's own 1–5 rating of the relationship. */
  rating: number | null
  status: ClientStatus
  createdAt: string
  updatedAt: string
}

const required = (max: number) => v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(max))
const phone = v.pipe(v.string(), v.trim(), v.regex(/^[+0-9()\-\s]{6,30}$/, 'Use digits, spaces and + ( ) - only.'))
/** Clearable text: a string to set it, null to clear it, absent to leave it. */
const nullableText = (max: number) => v.union([v.pipe(v.string(), v.trim(), v.maxLength(max)), v.null()])

export const createClientSchema = v.object({
  name: required(120),
  companyName: v.optional(nullableText(120)),
  discordServerId: v.optional(
    v.union([v.pipe(v.string(), v.trim(), v.regex(/^\d{5,25}$/, 'A Discord server id is digits only.')), v.null()]),
  ),
  discordServerName: v.optional(nullableText(120)),
  contactPerson: v.optional(nullableText(120)),
  contactEmail: v.optional(v.union([v.pipe(v.string(), v.trim(), v.toLowerCase(), v.email(), v.maxLength(254)), v.null()])),
  contactPhone: v.optional(v.union([phone, v.null()])),
  websiteUrl: v.optional(nullableText(200)),
  countryCode: v.optional(
    v.union([v.pipe(v.string(), v.trim(), v.regex(/^[A-Za-z]{2}$/, 'Two letters.'), v.toUpperCase()), v.null()]),
  ),
  timezone: v.optional(nullableText(60)),
  notes: v.optional(nullableText(5000)),
})

export type CreateClientInput = v.InferOutput<typeof createClientSchema>

/**
 * The mid-edit contract: any subset of the record, at any time. Absent means
 * unchanged, null means cleared. At least one change is required — a request
 * that changes nothing is a mistake, and the API says so rather than
 * pretending to agree.
 */
export const updateClientSchema = v.pipe(
  v.object({
    name: v.optional(required(120)),
    companyName: v.optional(nullableText(120)),
    discordServerId: v.optional(
      v.union([v.pipe(v.string(), v.trim(), v.regex(/^\d{5,25}$/, 'A Discord server id is digits only.')), v.null()]),
    ),
    discordServerName: v.optional(nullableText(120)),
    contactPerson: v.optional(nullableText(120)),
    contactEmail: v.optional(v.union([v.pipe(v.string(), v.trim(), v.toLowerCase(), v.email(), v.maxLength(254)), v.null()])),
    contactPhone: v.optional(v.union([phone, v.null()])),
    websiteUrl: v.optional(nullableText(200)),
    countryCode: v.optional(
      v.union([v.pipe(v.string(), v.trim(), v.regex(/^[A-Za-z]{2}$/, 'Two letters.'), v.toUpperCase()), v.null()]),
    ),
    timezone: v.optional(nullableText(60)),
    notes: v.optional(nullableText(5000)),
    rating: v.optional(v.union([v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(5)), v.null()])),
  }),
  v.check((input) => Object.values(input).some((value) => value !== undefined), 'Nothing to change.'),
)

export type UpdateClientInput = v.InferOutput<typeof updateClientSchema>

/** The working-status toggle. Archiving is its own decision, named for itself. */
export const setClientStatusSchema = v.object({
  status: v.picklist(['active', 'inactive'] satisfies readonly ('active' | 'inactive')[]),
})
